/**
 * Advent calendar interactivity:
 *   - copy discount code → clipboard with feedback
 *   - confetti animation on 'gift' popups
 *   - AJAX add-to-cart on 'atc' popups (with cart drawer if available)
 *   - decorative SVG path generation between cards
 *
 * Popup controls are delegated on document because the theme's <modal-dialog>
 * moves itself to <body> on connectedCallback (see global.js ~L590-594).
 * Per-instance listeners attached inside the <advent-day> subtree would get
 * orphaned when the modal jumps to body.
 *
 * Server-side gating already prevents future-day content from reaching
 * the client. This file only handles UX of already-rendered popups.
 */

class AdventDay extends HTMLElement {
  connectedCallback() {
    if (this.dataset.adventDayReady === 'true') return;
    this.dataset.adventDayReady = 'true';

    const opener = this.querySelector('modal-opener[data-modal]');
    const button = opener?.querySelector('button');
    if (!opener || !button) return;

    button.addEventListener('click', () => {
      const modalSelector = opener.getAttribute('data-modal');
      if (!modalSelector) return;

      const modal = document.querySelector(modalSelector);
      if (!modal) return;

      window.requestAnimationFrame(() => {
        if (!modal.hasAttribute('open')) return;

        const confettiContainer = modal.querySelector('[data-advent-confetti]');
        if (confettiContainer && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          fireConfetti(confettiContainer);
        }

        hydrateAdventKlaviyoForms(modal);
      });
    });
  }
}
customElements.define('advent-day', AdventDay);

let adventScrollFrame = null;

function easeInOutCubic(progress) {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;
}

function getCenteredScrollY(element) {
  const rect = element.getBoundingClientRect();
  const startY = window.scrollY || window.pageYOffset;
  const targetY = startY + rect.top - (window.innerHeight - rect.height) / 2;
  const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

  return Math.max(0, Math.min(targetY, maxY));
}

function scrollToAdventDay(day) {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const startY = window.scrollY || window.pageYOffset;
  const targetY = getCenteredScrollY(day);
  const distance = targetY - startY;

  if (adventScrollFrame) window.cancelAnimationFrame(adventScrollFrame);

  if (prefersReducedMotion || Math.abs(distance) < 2) {
    window.scrollTo(0, targetY);
    return;
  }

  const duration = Math.min(1100, Math.max(450, Math.abs(distance) * 0.55));
  const startTime = performance.now();

  function tick(now) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOutCubic(progress);

    window.scrollTo(0, startY + distance * easedProgress);

    if (progress < 1) {
      adventScrollFrame = window.requestAnimationFrame(tick);
    } else {
      adventScrollFrame = null;
    }
  }

  adventScrollFrame = window.requestAnimationFrame(tick);
}

function getAdventHashTarget(hash = window.location.hash) {
  if (!/^#dia-\d+$/.test(hash)) return null;
  return document.getElementById(hash.slice(1));
}

document.addEventListener('click', (event) => {
  const link = event.target.closest?.('.advent-calendar__today-cta[href^="#dia-"]');
  if (!link) return;

  const target = getAdventHashTarget(link.getAttribute('href'));
  if (!target) return;

  event.preventDefault();
  scrollToAdventDay(target);
  history.pushState(null, '', link.getAttribute('href'));
});

// ── Tooltips for past / future days (Duolingo-style) ───────────────────
// Click on a past or future day's button → toggle tooltip on its parent
// .advent-calendar__day. Click anywhere else (or another day) → close all
// open tooltips. Escape also closes. No auto-timeout: persistent until
// explicitly dismissed, matching Duolingo's behavior.
function closeAllAdventTooltips(except = null) {
  for (const open of document.querySelectorAll('.advent-calendar__day.is-tooltip-open')) {
    if (open !== except) open.classList.remove('is-tooltip-open');
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('[data-advent-tooltip-trigger]');
  const day = trigger?.closest('.advent-calendar__day');

  if (!day || !day.querySelector('.advent-calendar__tooltip')) {
    // Click landed somewhere that isn't a tooltip trigger → close everything.
    closeAllAdventTooltips();
    return;
  }

  event.preventDefault();
  closeAllAdventTooltips(day);
  day.classList.toggle('is-tooltip-open');
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeAllAdventTooltips();
});

function isInsideExpiredPopup(element) {
  return Boolean(element?.closest?.('.advent-calendar__popup-body.is-expired'));
}

document.addEventListener('click', (event) => {
  const expiredControl = event.target.closest?.(
    '.advent-calendar__popup-body.is-expired a, ' +
    '.advent-calendar__popup-body.is-expired button, ' +
    '.advent-calendar__popup-body.is-expired input, ' +
    '.advent-calendar__popup-body.is-expired select, ' +
    '.advent-calendar__popup-body.is-expired textarea, ' +
    '.advent-calendar__popup-body.is-expired video'
  );
  if (!expiredControl) return;

  event.preventDefault();
  event.stopPropagation();
}, true);

// Copy discount code to clipboard.
document.addEventListener('click', async (event) => {
  const button = event.target.closest?.('[data-advent-copy-button]');
  if (!button) return;
  event.preventDefault();
  if (isInsideExpiredPopup(button)) return;

  const wrapper = button.closest('[data-advent-copy]');
  if (!wrapper) return;

  const codeEl = wrapper.querySelector('.advent-calendar__code-value');
  const feedback = wrapper.querySelector('.advent-calendar__code-feedback');
  const label = button.querySelector('[data-advent-copy-label]');
  if (!codeEl) return;

  const code = codeEl.textContent.trim();
  const initialLabel = label?.textContent ?? '';
  const copiedLabel = button.dataset.copiedLabel || '✓';
  const copiedFeedback = button.dataset.copiedFeedback || '';
  const errorFeedback = button.dataset.errorFeedback || '';

  try {
    await navigator.clipboard.writeText(code);
    if (label && copiedLabel) label.textContent = copiedLabel;
    if (feedback) feedback.textContent = copiedFeedback;
    setTimeout(() => {
      if (label && initialLabel) label.textContent = initialLabel;
      if (feedback) feedback.textContent = '';
    }, 2500);
  } catch (error) {
    if (feedback) feedback.textContent = errorFeedback;
    console.error('[advent] clipboard error:', error);
  }
});

const klaviyoFallbackTimers = new WeakMap();

function getAdventKlaviyoFormId(wrapper) {
  return wrapper?.dataset.adventKlaviyoFormId?.trim() || '';
}

function hasRenderedKlaviyoForm(wrapper) {
  const embed = wrapper.querySelector('[data-advent-klaviyo-embed]');

  return Boolean(
    embed?.children.length ||
    wrapper.querySelector('form, iframe, [data-testid], .needsclick')
  );
}

function setKlaviyoFallbackVisible(wrapper, isVisible) {
  const fallback = wrapper.querySelector('[data-advent-klaviyo-open]');
  if (!fallback) return;

  fallback.hidden = !isVisible;
  wrapper.classList.toggle('is-klaviyo-missing', isVisible);
}

function hydrateAdventKlaviyoForms(root = document) {
  const wrappers = root.querySelectorAll('[data-advent-klaviyo-form-id]');
  for (const wrapper of wrappers) {
    const formId = getAdventKlaviyoFormId(wrapper);
    if (!formId) continue;

    window.clearTimeout(klaviyoFallbackTimers.get(wrapper));
    setKlaviyoFallbackVisible(wrapper, false);

    const timer = window.setTimeout(() => {
      setKlaviyoFallbackVisible(wrapper, !hasRenderedKlaviyoForm(wrapper));
    }, 2200);

    klaviyoFallbackTimers.set(wrapper, timer);
  }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-advent-klaviyo-open]');
  if (!button) return;
  event.preventDefault();
  if (isInsideExpiredPopup(button)) return;

  const wrapper = button.closest('[data-advent-klaviyo-form-id]');
  const formId = getAdventKlaviyoFormId(wrapper);
  if (!formId) return;

  window._klOnsite = window._klOnsite || [];
  window._klOnsite.push(['openForm', formId]);
});

// One AbortController per form so an in-flight submit gets cancelled when
// the user re-submits before it completes. WeakMap keyed by form node so
// entries GC themselves with the DOM.
const atcAbortControllers = new WeakMap();

function getAdventAtcDiscountCode(form) {
  return form?.dataset.adventDiscountCode?.trim() || '';
}

async function applyAdventDiscountCode(code, signal) {
  const encodedCode = encodeURIComponent(code);
  document.cookie = `discount_code=${encodedCode}; path=/`;

  const response = await fetch(`/discount/${encodedCode}`, {
    credentials: 'same-origin',
    signal,
  });

  if (!response.ok) throw new Error(`Discount failed: ${response.status}`);
}

async function fetchAdventCartDrawerState(cartDrawer, signal) {
  const formData = new FormData();
  formData.append(
    'sections',
    cartDrawer.getSectionsToRender().map((section) => section.id)
  );
  formData.append('sections_url', window.location.pathname);

  const config = typeof fetchConfig === 'function'
    ? fetchConfig('javascript')
    : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      };

  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];
  config.body = formData;
  config.signal = signal;

  const response = await fetch(window.routes?.cart_update_url ?? '/cart/update.js', config);
  const parsedState = await response.json();

  if (!response.ok || parsedState.status) {
    throw new Error(parsedState.description || parsedState.message || `Cart refresh failed: ${response.status}`);
  }

  return parsedState;
}

document.addEventListener('submit', async (event) => {
  const form = event.target.closest?.('[data-advent-atc-form]');
  if (!form) return;
  event.preventDefault();
  if (isInsideExpiredPopup(form)) return;

  const submitButton = form.querySelector('button[type="submit"]');
  if (submitButton) submitButton.disabled = true;

  atcAbortControllers.get(form)?.abort();
  const ctrl = new AbortController();
  atcAbortControllers.set(form, ctrl);

  try {
    const cartDrawer = document.querySelector('cart-drawer');
    const canRenderCartDrawer = cartDrawer &&
      typeof cartDrawer.renderContents === 'function' &&
      typeof cartDrawer.getSectionsToRender === 'function';
    const formData = new FormData(form);
    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        };

    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;
    config.signal = ctrl.signal;

    if (canRenderCartDrawer) {
      formData.append(
        'sections',
        cartDrawer.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cartDrawer.setActiveElement === 'function') cartDrawer.setActiveElement(document.activeElement);
    }

    const response = await fetch(form.action, config);
    const parsedState = await response.json();

    if (!response.ok || parsedState.status) {
      throw new Error(parsedState.description || parsedState.message || `ATC failed: ${response.status}`);
    }

    const discountCode = getAdventAtcDiscountCode(form);
    let cartState = parsedState;

    if (discountCode) {
      try {
        await applyAdventDiscountCode(discountCode, ctrl.signal);
        if (canRenderCartDrawer) {
          cartState = await fetchAdventCartDrawerState(cartDrawer, ctrl.signal);
        }
      } catch (discountError) {
        if (discountError.name === 'AbortError') throw discountError;
        console.error('[advent] discount error:', discountError);
      }
    }

    if (canRenderCartDrawer) {
      cartDrawer.renderContents(cartState);
    } else {
      window.location.href = window.routes?.cart_url ?? '/cart';
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('[advent] ATC error:', error);
      if (submitButton) submitButton.disabled = false;
    }
  }
});

function fireConfetti(container) {
  const count = 40;
  const colors = ['#FFE037', '#FF5336', '#001A57', '#FFCB00', '#FFEBBE'];
  container.replaceChildren();
  for (let i = 0; i < count; i++) {
    const piece = document.createElement('span');
    piece.className = 'advent-confetti-piece';
    piece.style.color = colors[i % colors.length];
    piece.style.insetInlineStart = `${Math.random() * 100}%`;
    piece.style.animationDelay = `${Math.random() * 600}ms`;
    piece.style.animationDuration = `${1800 + Math.random() * 1500}ms`;
    piece.style.transform = `rotate(${Math.random() * 360}deg)`;
    piece.addEventListener('animationend', () => piece.remove(), { once: true });
    container.appendChild(piece);
  }
}

/**
 * Decorative SVG path generator.
 * Generates a smooth wave path that snakes through the grid container.
 * Pure decoration — does NOT need to perfectly intersect each card center.
 */
class AdventOverlay {
  constructor(section) {
    this.section = section;
    this.svg = section.querySelector('.advent-calendar__overlay');
    this.grid = section.querySelector('.advent-calendar__grid');
    if (!this.svg || !this.grid) return;

    this.#draw();

    const debouncedDraw = AdventOverlay.#debounce(this.#draw.bind(this), 200);

    // Window resize: covers viewport changes that reflow the grid.
    window.addEventListener('resize', debouncedDraw);

    // ResizeObserver on the grid: covers grid container size changes
    // (e.g. theme editor swapping the section width).
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(debouncedDraw);
      this.resizeObserver.observe(this.grid);
    }

    // Theme editor changes positioning by mutating style/class on day/spacer
    // nodes. Observing only those direct children avoids reacting to our own
    // SVG path redraws.
    this.mutationObserver = new MutationObserver(debouncedDraw);
    for (const item of this.grid.children) {
      if (
        item.classList.contains('advent-calendar__day') ||
        item.classList.contains('advent-calendar__spacer')
      ) {
        this.mutationObserver.observe(item, {
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
      }
    }
  }

  #draw() {
    const { width, height } = this.grid.getBoundingClientRect();
    if (!width || !height) return;

    this.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

    const cards = this.grid.querySelectorAll('.advent-calendar__day');
    if (cards.length < 2) {
      this.svg.replaceChildren();
      return;
    }

    const gridRect = this.grid.getBoundingClientRect();
    const points = Array.from(cards).map((card) => {
      const rect = card.getBoundingClientRect();
      return {
        x: rect.left - gridRect.left + rect.width / 2,
        y: rect.top - gridRect.top + rect.height / 2,
        r: Math.min(rect.width, rect.height) / 2,
      };
    });

    // Continuous serpentine: single M + chained Q/T (original wave shape).
    let d = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
    for (let i = 1; i < points.length; i++) {
      const p0 = points[i - 1];
      const p1 = points[i];
      const cx = (p0.x + p1.x) / 2;
      const cy = (p0.y + p1.y) / 2;
      d += ` Q ${p0.x.toFixed(1)} ${cy.toFixed(1)} ${cx.toFixed(1)} ${cy.toFixed(1)}`;
      d += ` T ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
    }

    this.svg.replaceChildren();

    // No mask — let the path flow through card centers as in the original.
    // The opaque SVG icons (z-index 1) cover most of the path inside their
    // bounding boxes; small visible bits in transparent corner padding are
    // the trade-off for the smooth continuous serpentine.
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    this.svg.appendChild(path);
  }

  static #debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }
}

document.addEventListener('DOMContentLoaded', () => {
  for (const section of document.querySelectorAll('.advent-calendar')) {
    new AdventOverlay(section);
  }

  const hashTarget = getAdventHashTarget();
  if (hashTarget) {
    window.requestAnimationFrame(() => scrollToAdventDay(hashTarget));
  }
});

// Theme editor: re-init when Shopify swaps the section HTML
// (block reorder, section duplicate, etc.).
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.advent-calendar');
  if (section) new AdventOverlay(section);
});
